import React, { useState, useCallback, useRef, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component";
import "./LazyImage.scss";

export interface LazyImageProps {
  src: string;
  alt?: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  placeholder?: string;
  effect?: "blur" | "black-and-white" | "opacity";
  noPlaceholder?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
  maxRetries?: number; // 最大重试次数
  retryDelay?: number; // 重试延迟（毫秒）
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt = "",
  className,
  width = "100%",
  height = "100%",
  placeholder,
  effect = "blur",
  noPlaceholder = false,
  style,
  onClick,
  maxRetries = 3,
  retryDelay = 1000,
}) => {
  const defaultPlaceholder = "/src/assets/common/placeholder.png";
  const [imageSrc, setImageSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 当src变化时重置状态
  useEffect(() => {
    setImageSrc(src);
    setRetryCount(0);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, [src]);

  // 处理图片加载错误
  const handleError = useCallback(() => {
    if (retryCount < maxRetries) {
      retryTimeoutRef.current = setTimeout(
        () => {
          setRetryCount(prev => prev + 1);
          // 添加时间戳强制刷新
          const separator = src.includes("?") ? "&" : "?";
          setImageSrc(`${src}${separator}_retry=${Date.now()}`);
        },
        retryDelay * (retryCount + 1)
      ); // 递增延迟
    }
  }, [src, retryCount, maxRetries, retryDelay]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // 监听图片重载事件
  useEffect(() => {
    const handleReload = () => {
      // 只重载失败的图片
      if (retryCount > 0) {
        setRetryCount(0);
        // 添加时间戳强制刷新
        const separator = src.includes("?") ? "&" : "?";
        setImageSrc(`${src}${separator}_reload=${Date.now()}`);
      }
    };

    window.addEventListener("image-reload-trigger", handleReload);
    return () => {
      window.removeEventListener("image-reload-trigger", handleReload);
    };
  }, [retryCount, src]);

  return (
    <div
      className={`lazy-load-image-wrapper ${className || ""}`}
      style={{ width, height, ...style }}
      onClick={onClick}
    >
      {/* @ts-expect-error - LazyLoadImage type compatibility with React 19 */}
      <LazyLoadImage
        src={imageSrc}
        alt={alt}
        width="100%"
        height="100%"
        effect={effect}
        wrapperClassName="lazy-load-image-placeholder"
        wrapperProps={{
          style: {
            width: "100%",
            height: "100%",
            display: "block",
          },
        }}
        placeholderSrc={
          noPlaceholder ? undefined : placeholder || defaultPlaceholder
        }
        threshold={100}
        useIntersectionObserver={true}
        onError={handleError}
      />
    </div>
  );
};

export default LazyImage;
